/**
 * Página do Dashboard
 * 
 * @author Equipe GestaoeCRM
 * @date 25/09/2025
 */

import { useState, useEffect } from 'react';
import { useAuth } from '@/contexts/AuthContext';
import { useTenant } from '@/contexts/TenantContext';
import { useQuery } from '@tanstack/react-query';
import { dashboardApi } from '@/services/api';
import { getUserStats } from '@/services/workSessionReportsApi';
import { FolderOpen, ClipboardList, Clock, Users, Building2, Settings, RefreshCw } from 'lucide-react';
import LoadingSpinner from '@/components/LoadingSpinner';
import LoadingState from '@/components/LoadingState';
import EmptyState from '@/components/EmptyState';
import WorkSessionReportsWidget from '@/components/WorkSessionReportsWidget';
import AnalystWorkSessionReportsWidget from '@/components/AnalystWorkSessionReportsWidget';
import CompanyActiveOSGrid from '@/components/CompanyActiveOSGrid';
import CompanyWorkSessionReports from '@/components/CompanyWorkSessionReports';
import ProjectCardsList from '@/components/ProjectCardsList';
import AdminAreaCards from '@/components/AdminAreaCards';
import AdminDashboardCharts from '@/components/AdminDashboardCharts';
import DashboardWidgetConfigModal from '@/components/DashboardWidgetConfigModal';
import { useDashboardWidgets } from '@/hooks/useDashboardWidgets';

export default function DashboardPage() {
  const { user } = useAuth();
  const { currentTenant } = useTenant();
  const { isWidgetVisible } = useDashboardWidgets();
  const [showConfigModal, setShowConfigModal] = useState(false);
  const [lastUpdate, setLastUpdate] = useState<Date>(new Date());

  // Buscar dados do dashboard com atualização em tempo real
  const { data: dashboardData, isLoading, error, refetch, dataUpdatedAt } = useQuery({
    queryKey: ['dashboard-stats', user?.id],
    queryFn: dashboardApi.getStats,
    enabled: !!user,
    refetchInterval: 30 * 1000, // Atualizar a cada 30 segundos (melhorado de 5 minutos)
    staleTime: 20 * 1000, // Considerar dados frescos por 20 segundos
  });

  // Atualizar timestamp quando dados mudarem
  useEffect(() => {
    if (dataUpdatedAt) {
      setLastUpdate(new Date(dataUpdatedAt));
    }
  }, [dataUpdatedAt]);

  // Buscar estatísticas do usuário para analistas (projetos e demandas)
  const { data: userStatsData } = useQuery({
    queryKey: ['user-stats-today', user?.id],
    queryFn: () => getUserStats('today'),
    enabled: !!user && user.role === 'ANALISTA',
    refetchInterval: 60 * 1000, // Atualizar a cada minuto
  });

  if (isLoading) {
    return (
      <div className="space-y-4">
        <div className="md:flex md:items-center md:justify-between">
          <div className="flex-1 min-w-0">
            <h2 className="text-xl font-semibold leading-6 text-text-primary sm:text-2xl sm:truncate tracking-tight">
              Dashboard
            </h2>
            <p className="mt-1 text-sm text-text-muted">
              Carregando dados...
            </p>
          </div>
        </div>
        <LoadingState message="Carregando dashboard..." size="lg" />
      </div>
    );
  }

  if (error) {
    return (
      <div className="space-y-4">
        <div className="md:flex md:items-center md:justify-between">
          <div className="flex-1 min-w-0">
            <h2 className="text-xl font-semibold leading-6 text-text-primary sm:text-2xl sm:truncate tracking-tight">
              Dashboard
            </h2>
            <p className="mt-1 text-sm text-red-400">
              Erro ao carregar dados do dashboard
            </p>
          </div>
        </div>
      </div>
    );
  }

  const stats = dashboardData?.data?.data;
  const userStats = userStatsData?.data;
  
  // Preparar cards diferentes para analista
  const analystStatsCards = user?.role === 'ANALISTA' && userStats ? [
    {
      name: 'Projetos Ativos',
      value: userStats.activeProjects?.toString() || '0',
      icon: FolderOpen,
      change: `+${userStats.demandsWithoutProject || 0}`,
      changeType: 'neutral' as const,
      subtitle: 'demandas sem projeto',
    },
    {
      name: 'Demandas Pendentes',
      value: stats?.stats?.demands?.pending?.toString() || '0',
      icon: ClipboardList,
      change: stats?.stats?.demands?.change || '+0',
      changeType: stats?.stats?.demands?.changeType || 'neutral' as const,
    },
    {
      name: 'Horas Trabalhadas',
      value: `${userStats.totalHours?.toFixed(1) || 0}h`,
      icon: Clock,
      change: stats?.stats?.hours?.change || '+0h',
      changeType: stats?.stats?.hours?.changeType || 'neutral' as const,
    },
  ] : [];

  const statsCards = [
    {
      name: 'Projetos Ativos',
      value: stats?.stats?.projects?.active?.toString() || '0',
      icon: FolderOpen,
      change: stats?.stats?.projects?.change || '+0',
      changeType: stats?.stats?.projects?.changeType || 'neutral' as const,
    },
    {
      name: 'Demandas Pendentes',
      value: stats?.stats?.demands?.pending?.toString() || '0',
      icon: ClipboardList,
      change: stats?.stats?.demands?.change || '+0',
      changeType: stats?.stats?.demands?.changeType || 'neutral' as const,
    },
    {
      name: 'Horas Trabalhadas',
      value: stats?.stats?.hours?.formatted || '0h',
      icon: Clock,
      change: stats?.stats?.hours?.change || '+0h',
      changeType: stats?.stats?.hours?.changeType || 'neutral' as const,
    },
    {
      name: 'Usuários Ativos',
      value: stats?.stats?.users?.active?.toString() || '0',
      icon: Users,
      change: stats?.stats?.users?.change || '+0',
      changeType: stats?.stats?.users?.changeType || 'neutral' as const,
    },
  ];

  // Filtrar estatísticas baseado no role do usuário
  const filteredStats = user?.role === 'ANALISTA' 
    ? analystStatsCards
    : statsCards;

  return (
    <div className="space-y-4">
      {/* Header */}
      <div className="md:flex md:items-center md:justify-between">
        <div className="flex-1 min-w-0">
          <div className="flex items-center space-x-3">
            <h2 className="text-xl font-semibold leading-6 text-text-primary sm:text-2xl sm:truncate tracking-tight">
              Dashboard
            </h2>
            <button
              onClick={() => refetch()}
              className="p-1.5 rounded-button hover:bg-background-glass-hover transition-colors duration-200"
              title="Atualizar dados"
            >
              <RefreshCw className="h-4 w-4 text-text-secondary" />
            </button>
            <button
              onClick={() => setShowConfigModal(true)}
              className="p-1.5 rounded-button hover:bg-background-glass-hover transition-colors duration-200"
              title="Personalizar widgets"
            >
              <Settings className="h-4 w-4 text-text-secondary" />
            </button>
          </div>
          <p className="mt-1 text-sm text-text-muted">
            Bem-vindo, {user?.name}! Aqui está um resumo do seu trabalho.
          </p>
          <div className="mt-2 flex items-center space-x-4">
            {currentTenant && (
              <div className="flex items-center text-sm text-text-secondary">
                <Building2 className="h-4 w-4 mr-2" />
                <span className="font-semibold">{currentTenant.companyName}</span>
                {currentTenant.tradeName && (
                  <span className="ml-1 text-text-muted">({currentTenant.tradeName})</span>
                )}
              </div>
            )}
            <div className="text-xs text-text-muted">
              Última atualização: {lastUpdate.toLocaleTimeString('pt-BR')}
            </div>
          </div>
        </div>
      </div>

      {/* Stats - Widget personalizável */}
      {isWidgetVisible('stats-cards') && (
        <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-4">
          {filteredStats.map((item) => (
          <div key={item.name} className="stat-card group">
            <div className="flex items-center justify-between">
              <div className="flex items-center space-x-3">
                <div className="p-2 rounded-xl bg-gradient-to-br from-blue-500/20 to-purple-500/20 border border-blue-500/30 group-hover:from-blue-500/30 group-hover:to-purple-500/30 transition-all duration-300">
                  <item.icon className="h-5 w-5 text-blue-400" />
                </div>
                <div>
                  <p className="text-xs font-medium text-gray-400 uppercase tracking-wide">
                    {item.name}
                  </p>
                  <p className="text-2xl font-bold text-white group-hover:text-blue-400 transition-colors duration-300">
                    {item.value}
                  </p>
                </div>
              </div>
              <div className={`text-right ${
                item.changeType === 'positive' ? 'text-green-400' : 
                item.changeType === 'negative' ? 'text-red-400' : 
                'text-gray-400'
              }`}>
                <p className="text-sm font-semibold">{item.change}</p>
              </div>
            </div>
            </div>
          ))}
        </div>
      )}

      {/* Conteúdo principal baseado no role */}
      {user?.role === 'ANALISTA' ? (
        // Dashboard para Analistas - focado em projetos e relatórios
        <div className="space-y-4">
          {/* Projetos Recentes */}
          {isWidgetVisible('recent-projects') && (
            <div className="card">
              <div className="card-header">
                <h3 className="card-title">Projetos Recentes</h3>
                <p className="card-description">
                  Últimos projetos em que você está trabalhando
                </p>
              </div>
              <div className="card-content">
                {stats?.recentProjects?.length > 0 ? (
                  <div className="space-y-4">
                    {stats.recentProjects.map((project: any) => (
                      <div key={project.id} className="flex items-center justify-between p-3 bg-background-glass-hover rounded-glass border border-border-glass/50 hover:border-secondary/30 transition-all duration-300">
                        <div>
                          <h4 className="font-semibold text-text-primary">{project.name}</h4>
                          <p className="text-sm text-text-muted">{project.clientName}</p>
                        </div>
                        <span className={`badge ${
                          project.status === 'DESENVOLVIMENTO' ? 'badge-secondary' :
                          project.status === 'PLANEJAMENTO' ? 'badge-primary' :
                          project.status === 'TESTE' ? 'badge-accent' :
                          project.status === 'CONCLUIDO' ? 'badge-success' :
                          'badge-warning'
                        }`}>
                          {project.status === 'DESENVOLVIMENTO' ? 'Em andamento' :
                           project.status === 'PLANEJAMENTO' ? 'Planejamento' :
                           project.status === 'TESTE' ? 'Teste' :
                           project.status === 'CONCLUIDO' ? 'Concluído' :
                           'Pausado'}
                        </span>
                      </div>
                    ))}
                  </div>
                ) : (
                  <EmptyState
                    title="Nenhum projeto encontrado"
                    description="Você ainda não está trabalhando em nenhum projeto"
                  />
                )}
              </div>
            </div>
          )}

          {/* Widget de Relatórios de Sessões - Versão para Analista */}
          {isWidgetVisible('analyst-reports') && <AnalystWorkSessionReportsWidget />}
        </div>
      ) : user?.role === 'GESTOR' ? (
        // Dashboard para GESTOR - interface específica com cards de projetos
        <div className="space-y-4">
          {/* Cards de Projetos */}
          {isWidgetVisible('project-cards') && (
            <div className="card">
              <div className="card-header">
                <h3 className="card-title">Meus Projetos</h3>
                <p className="card-description">
                  Visualize seus projetos e acompanhe os jobs/tasks ativos em cada um
                </p>
              </div>
              <div className="card-content">
                <ProjectCardsList />
              </div>
            </div>
          )}
        </div>
      ) : (
        // Dashboard para ADMIN - visualização hierárquica por áreas
        <div className="space-y-4">
          {/* Gráficos de OS/JOB/TASK */}
          {isWidgetVisible('charts') && <AdminDashboardCharts />}

          {/* Cards de Áreas com Navegação Hierárquica */}
          {isWidgetVisible('areas') && (
            <div className="card">
              <div className="card-header">
                <h3 className="card-title">Visualização por Áreas</h3>
                <p className="card-description">
                  Navegue pela estrutura da empresa: Áreas → Projetos → Demandas → Jobs/Tasks Ativos
                </p>
              </div>
              <div className="card-content">
                <AdminAreaCards />
              </div>
            </div>
          )}

          {/* Grade de Jobs/Tasks Ativos da Empresa */}
          {isWidgetVisible('active-os-grid') && (
            <div className="card">
              <div className="card-header">
                <h3 className="card-title">Jobs/Tasks Ativos da Empresa</h3>
                <p className="card-description">
                  Todos os jobs/tasks em andamento no tenant
                </p>
              </div>
              <div className="card-content">
                <CompanyActiveOSGrid />
              </div>
            </div>
          )}

          {/* Relatórios de Sessões da Empresa */}
          {isWidgetVisible('work-session-reports') && (
            <div className="card">
              <div className="card-header">
                <h3 className="card-title">Relatórios de Sessões da Empresa</h3>
                <p className="card-description">
                  Estatísticas de trabalho de todos os funcionários
                </p>
              </div>
              <div className="card-content">
                <CompanyWorkSessionReports />
              </div>
            </div>
          )}
        </div>
      )}

      {/* Modal de Configuração de Widgets */}
      <DashboardWidgetConfigModal
        isOpen={showConfigModal}
        onClose={() => setShowConfigModal(false)}
      />
    </div>
  );
}
